// Ring Frame Time & Motion study API client — Phase 1.
// Backend: app/api/routers/live_study.py (rf_* endpoints under /hr/studies).
import apiClient from '@/shared/api/client'

export type RfSide = 'RHS' | 'LHS'

export interface RfRound {
  id: string
  side: RfSide
  round_no: number
  creel_open: number
  cots_jam: number
  spindle_speed: number | null
  started_at: string | null
  ended_at: string | null
  duration_seconds: number | null
}

export interface RfBreakEvent {
  id: string
  round_id: string | null
  side: RfSide
  occurred_at: string
  attended: boolean
  piecing_attempts: number
  successful: boolean | null
  is_repeat: boolean
  em_seconds: number
  mend_open: boolean
}

export interface RfResult {
  ends_attended: number
  ends_left_down: number
  ends_found_down: number
  total_end_breaks: number
  piecing_attempts: number
  successful_piecings: number
  repeat_breaks: number
  total_em_seconds: number
  avg_piecing_sec: number | null
  break_attendance_pct: number | null
  successful_piecing_pct: number | null
  repeat_break_pct: number | null
  running_spindle_hours: number | null
  /** Whole-frame spindle-hours scaled to the side(s) actually patrolled —
   *  this is the breakage-rate denominator. */
  observed_spindle_hours: number | null
  sides_studied: string[]
  breakage_per_1000_sh: number | null
  breakage_per_100_sh: number | null
  ends_down_pct: number | null
  running_spindles_observed: number
  total_A_ends: number
  total_B_ends: number
  total_creel_open: number
  total_cots_jam: number
  rounds_rhs: number
  rounds_lhs: number
  patrol_cycle_sec: { avg: number | null; min: number | null; max: number | null }
  losses_by_bucket: Record<string, number>
  free_time_by_owner: Record<string, number>
  creeling: { bobbins?: number; minutes?: number; sec_per_bobbin?: number }
  typed_breakage_per_1000_sh: number | null
  breakage_gap_vs_typed: number | null
  breakdowns: Record<string, Record<string, number>>
}

export interface RfContext {
  machine_code: string | null
  machine_make: string | null
  machine_model: string | null
  installed_spindles: number | null
  running_spindles: number | null
  count_name: string | null
  lot: string | null
  employee_name: string | null
  employee_code: string | null
  shift_name: string | null
  shift_start: string | null
  shift_end: string | null
  break_minutes: number | null
  working_minutes: number | null
  allocation_found: boolean
  certification_status: string | null
}

export interface RfLossItem {
  bucket: string
  minutes: number
  reason_code?: string | null
  loss_owner?: string | null
}

export interface RfIntervalItem {
  running_spindles: number
  running_minutes: number
}

export interface RfSubmitResponse {
  result: RfResult
  warnings: string[]
}

const P = '/hr/studies'

export const ringFrameStudyApi = {
  startRound: async (sessionId: string, side: RfSide, clientTapId: string) =>
    (await apiClient.post<RfRound>(
      `${P}/sessions/${sessionId}/rf/round/start`,
      { side, client_tap_id: clientTapId },
    )).data,

  startMend: async (sessionId: string, roundId: string, clientTapId: string) =>
    (await apiClient.post<RfBreakEvent>(
      `${P}/sessions/${sessionId}/rf/round/${roundId}/mend/start`,
      { client_tap_id: clientTapId },
    )).data,

  // `elapsedSeconds` is the mending duration the device itself measured at
  // the moment of the MEND/DONE taps — it must be captured by the caller
  // BEFORE this call is made (or queued offline), never derived here at
  // request time, or a replayed offline tap would record the queue delay
  // as mending time (see backend ring_frame_study_service.mend_stop).
  stopMend: async (
    sessionId: string, eventId: string, successful: boolean, clientTapId: string,
    elapsedSeconds: number,
  ) =>
    (await apiClient.post<RfBreakEvent>(
      `${P}/sessions/${sessionId}/rf/mend/${eventId}/stop`,
      { successful, client_tap_id: clientTapId, elapsed_seconds: elapsedSeconds },
    )).data,

  retryMend: async (sessionId: string, eventId: string, clientTapId: string) =>
    (await apiClient.post<RfBreakEvent>(
      `${P}/sessions/${sessionId}/rf/mend/${eventId}/retry`,
      { client_tap_id: clientTapId },
    )).data,

  repeatMend: async (sessionId: string, eventId: string, clientTapId: string) =>
    (await apiClient.post<RfBreakEvent>(
      `${P}/sessions/${sessionId}/rf/mend/${eventId}/repeat`,
      { client_tap_id: clientTapId },
    )).data,

  // `occurredAt` is an ISO timestamp captured on the device at tap time —
  // used so a tap queued offline and replayed later is stamped with the
  // real moment it happened, not the replay time.
  leftDown: async (sessionId: string, roundId: string, clientTapId: string, occurredAt: string) =>
    (await apiClient.post<RfBreakEvent>(
      `${P}/sessions/${sessionId}/rf/round/${roundId}/left-down`,
      { client_tap_id: clientTapId, occurred_at: occurredAt },
    )).data,

  tapCounter: async (sessionId: string, roundId: string, counter: string, clientTapId: string) =>
    (await apiClient.post<RfRound>(
      `${P}/sessions/${sessionId}/rf/round/${roundId}/counter`,
      { counter, client_tap_id: clientTapId },
    )).data,

  undoCounter: async (sessionId: string, roundId: string, counter: string, clientTapId: string) =>
    (await apiClient.post<RfRound>(
      `${P}/sessions/${sessionId}/rf/round/${roundId}/counter/undo`,
      { counter, client_tap_id: clientTapId },
    )).data,

  // `durationSeconds` is the round duration computed on the device from
  // when the round started, at the moment of the end-round tap — used
  // instead of server wall-clock for the same offline-replay reason as
  // stopMend above.
  endRound: async (sessionId: string, roundId: string, clientTapId: string, durationSeconds: number | null) =>
    (await apiClient.post<RfRound>(
      `${P}/sessions/${sessionId}/rf/round/${roundId}/end`,
      { client_tap_id: clientTapId, duration_seconds: durationSeconds },
    )).data,

  saveLosses: async (sessionId: string, losses: RfLossItem[]) =>
    (await apiClient.post<Array<{
      id: string
      bucket: string
      minutes: number
      reason_code: string | null
      loss_owner: string | null
    }>>(
      `${P}/sessions/${sessionId}/rf/losses`,
      { losses },
    )).data,

  saveCreeling: async (sessionId: string, bobbins: number, minutes?: number) =>
    (await apiClient.post(
      `${P}/sessions/${sessionId}/rf/creeling`,
      { bobbins, minutes },
    )).data,

  setSpindleIntervals: async (sessionId: string, intervals: RfIntervalItem[]) =>
    (await apiClient.post(
      `${P}/sessions/${sessionId}/rf/spindle-intervals`,
      { intervals },
    )).data,

  getResult: async (sessionId: string) =>
    (await apiClient.get<RfResult>(
      `${P}/sessions/${sessionId}/rf/result`,
    )).data,

  // On failure (HTTP 400) the response body is {errors: string[], warnings:
  // string[]} — let the axios error propagate so the caller can read
  // error.response.data.errors and render them; do not catch/reshape here.
  submitStudy: async (sessionId: string) =>
    (await apiClient.post<RfSubmitResponse>(
      `${P}/sessions/${sessionId}/rf/submit`,
    )).data,

  getContext: async (params: {
    employee_id?: string
    machine_id?: string
    shift_id?: string
    study_date?: string
  }) => (await apiClient.get<RfContext>(
    `${P}/ring-frame/context`, { params },
  )).data,
}
